import React, { useEffect, useMemo, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { DrawerBodyWrapper, TOAST_TYPE } from '@base'
import { getInputType } from '@base/composite/Table/TableUtils/Columns'
import { enquiryInputType, strings } from '@constants'
import { getFulfillmentPlanByIdApi } from '@data/conn/get/shipmentRequest'
import { showLoader, showToast } from '@data/state/action'
import { decimalSeparatedNumber, displayAmount, formatDate, formatMixedQuantities, formatPeriod } from '@utils'
import { isSalesAdmin } from '@utils/adminFilter'

import {
	Title,
	KeyValue,
	KeyLabel,
	KeyVal,
	DrawerSummaryCard,
	SummaryRow,
	DetailCard,
	DetailItem,
	DetailLabel,
	DetailValue,
	DetailTable,
} from '../styles'

// weights come in kgs for WEIGHT items, in their own unit otherwise
const displayItemWeight = (weight, inputType) => {
	if (weight == null) return '--'
	const value = inputType === enquiryInputType.WEIGHT ? Number(weight) / 1000 : Number(weight)
	return `${decimalSeparatedNumber(value)} ${getInputType(inputType)}`
}

const FulfillmentDetails = ({ row }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	// sales admins must not see which supplier the plan belongs to
	const hideSupplierName = isSalesAdmin(user?.role, false)
	const [details, setDetails] = useState(null)

	// for a fulfillment plan approval the reference id is the fulfillment plan id
	const fulfillmentPlanId = row?.reference_id

	useEffect(() => {
		if (!fulfillmentPlanId) return
		dispatch(showLoader(true))
		getFulfillmentPlanByIdApi(fulfillmentPlanId)
			.then((response) => setDetails(response))
			.catch((err) => {
				dispatch(showToast(true, err?.response?.data?.message || strings('something_went_wrong'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}, [fulfillmentPlanId])

	const plan = details?.fulfillment_plan
	const items = plan?.order_item_responses ?? []

	const detailFields = [
		{ label: strings('order_id'), value: details?.order_id || '--' },
		{ label: strings('buyer_po_number'), value: details?.original_buyer_po_number || '--' },
		...(hideSupplierName ? [] : [{ label: strings('supplier'), value: plan?.supplier?.organisation_name || '--' }]),
		{ label: strings('supplier_po'), value: details?.original_supplier_po_number || '--' },
		{ label: strings('supply_person'), value: plan?.supply_manager?.full_name || '--' },
		{ label: strings('responded_by'), value: plan?.responded_by?.full_name || '--' },
		{ label: strings('fulfillment_quantity'), value: formatMixedQuantities(items, 'fulfillment_weight') },
		{ label: strings('transit_time'), value: details?.transit_time != null ? `${details.transit_time} ${strings('days')}` : '--' },
		{ label: strings('status'), value: plan?.status || '--' },
		{ label: strings('expected_delivery_date'), value: formatDate(details?.expected_delivery_date) },
		{ label: strings('fulfillment_period'), value: formatPeriod(plan?.start_date, plan?.end_date), fullRow: true },
		{ label: strings('shipping_address'), value: details?.shipping_address?.complete_address || '--', fullRow: true },
	]

	const columns = useMemo(
		() => [
			{ label: strings('product'), key: (item) => item?.product_name || '--', sortable: false },
			// weight items come in kgs and show in MT, other items in their own unit
			{ label: strings('quantity'), key: (item) => displayItemWeight(item?.fulfillment_weight, item?.input_type), sortable: false },
			// gst rides along with the rate, same as the item rows on Create Shipment
			{
				label: strings('price_per_kg'),
				key: (item) => `${displayAmount(item?.price_per_kg)}${item?.gst != null ? ` (+${item.gst}%)` : ''}`,
				sortable: false,
			},
		],
		[]
	)

	return (
		<DrawerBodyWrapper>
			<DrawerSummaryCard>
				<Title $size='lg'>{fulfillmentPlanId || '--'}</Title>
				<SummaryRow>
					<KeyValue>
						<KeyLabel>{strings('fulfillment_amount')}</KeyLabel>
						<KeyVal $color='success'>{row?.metrics?.total_amount != null ? displayAmount(row.metrics.total_amount) : '--'}</KeyVal>
					</KeyValue>
				</SummaryRow>
			</DrawerSummaryCard>
			<DetailCard hoverable={false}>
				{detailFields.map((detail) => (
					<DetailItem key={detail.label} $fullRow={detail.fullRow}>
						<DetailLabel>{detail.label}</DetailLabel>
						<DetailValue>{detail.value}</DetailValue>
					</DetailItem>
				))}
			</DetailCard>
			<DetailTable columns={columns} data={items} emptyState={{ text: strings('no_data_found') }} hideFooter />
		</DrawerBodyWrapper>
	)
}

export default FulfillmentDetails
